import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Check, Link as LinkIcon, Loader2, Plus, RefreshCw, X } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import { buildEvidenceBulkItem } from '../../../components/EvidenceLinkBrowser'
import {
  buildTimelineEntryFromPatronActivity,
  buildTimelineEntryFromSuggestion,
  filterTimelineByScope,
  groupTimelineForLayout,
  LAYOUT_VIEWS,
  loadDismissedSuggestionKeys,
  mergePatronActivityTimeline,
  saveDismissedSuggestionKeys,
  toLocalDateYmd,
} from '../../../utils/patronActivityTimeline'
import { evidenceItemTypeLabel } from '../../../utils/evidenceBrowserLabels'

const P = { pri: '#0a5169', priM: '#e8f2f6' }
const fmt$ = (n) =>
  n == null || Number.isNaN(Number(n))
    ? null
    : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })

const SCOPE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'incident_day', label: 'Incident day' },
  { id: 'prior_visits', label: 'Prior visits' },
]

function scopeBadge(scope) {
  if (scope === 'incident_day') return { label: 'Incident day', bg: '#e8f2f6', color: P.pri }
  return { label: 'Prior visit', bg: '#f1f5f9', color: '#64748b' }
}

function statusBadge(status) {
  if (status === 'confirmed') return { label: 'Confirmed', bg: '#f0fdf4', color: '#16a34a' }
  return { label: 'Suggested', bg: '#fffbeb', color: '#d97706' }
}

function TimelineRow({
  caseId,
  entry,
  steps,
  stepTitleById,
  onConfirm,
  onDismiss,
  onConnectStep,
  confirmingKey,
  connectingKey,
}) {
  const scope = scopeBadge(entry.scope)
  const status = statusBadge(entry.status)
  const stepTitle = entry.connectedStepId ? stepTitleById.get(entry.connectedStepId) : null
  const busy = confirmingKey === entry.stableKey || connectingKey === entry.stableKey

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center gap-2 py-3 px-3 border rounded-lg mb-2 ${
        entry.status === 'suggested'
          ? 'border-amber-200 bg-amber-50/40'
          : entry.entryType === 'observation'
            ? 'border-violet-200 bg-violet-50/40'
            : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          <span className="text-xs font-semibold text-slate-800 tabular-nums">{entry.timeLabel}</span>
          <span
            className="text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide"
            style={{ background: status.bg, color: status.color }}
          >
            {status.label}
          </span>
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {entry.typeLabel}
          </span>
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
            style={{ background: scope.bg, color: scope.color }}
          >
            {scope.label}
          </span>
        </div>
        <div className="text-xs text-slate-600 flex flex-wrap gap-x-3 gap-y-0.5">
          {entry.egm && entry.egm !== '—' ? <span>EGM {entry.egm}</span> : null}
          {entry.memberId && entry.memberId !== '—' ? <span>Member {entry.memberId}</span> : null}
          {entry.amount != null ? <span className="font-semibold text-red-600">{fmt$(entry.amount)}</span> : null}
          {entry.linkReason && entry.entryType !== 'observation' ? (
            <span className="text-slate-400">{entry.linkReason}</span>
          ) : null}
        </div>
        {entry.observationText ? (
          <p className="text-xs text-slate-700 mt-1.5 whitespace-pre-wrap leading-relaxed">{entry.observationText}</p>
        ) : null}
        {entry.linkedEvidence?.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {entry.linkedEvidence.map((le) => (
              <li key={le.case_evidence_id} className="text-[11px] text-slate-600 flex items-center gap-1.5">
                <LinkIcon size={11} className="text-slate-400 shrink-0" />
                <span>
                  Linked {evidenceItemTypeLabel(le.item_type)} · {le.source_id || le.case_evidence_id}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {(entry.mediaList?.length ? entry.mediaList : entry.media ? [entry.media] : []).map((media, idx) => (
          media?.file_name && entry.observationId && caseId ? (
            <button
              key={`${entry.observationId}-${idx}-${media.file_name}`}
              type="button"
              onClick={async () => {
                try {
                  const res = await authFetch(
                    getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/patron-activity/${encodeURIComponent(entry.observationId)}/media?index=${idx}`)
                  )
                  if (!res.ok) throw new Error('Could not load attachment')
                  const blob = await res.blob()
                  const url = URL.createObjectURL(blob)
                  window.open(url, '_blank', 'noopener,noreferrer')
                  setTimeout(() => URL.revokeObjectURL(url), 60000)
                } catch (e) {
                  window.alert(e.message || 'Could not open attachment')
                }
              }}
              className="inline-flex text-[11px] font-semibold mt-1.5 text-[#0a5169] hover:underline"
            >
              View attachment — {media.file_name}
            </button>
          ) : null
        ))}
        {entry.connectedStepId && stepTitle ? (
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
            <LinkIcon size={11} />
            Step: {stepTitle}
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
        {entry.status === 'suggested' ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => onConfirm(entry)}
              className="text-xs py-1.5 px-2.5 rounded-md border font-semibold flex items-center gap-1 disabled:opacity-50"
              style={{ borderColor: '#16a34a', background: '#f0fdf4', color: '#16a34a' }}
            >
              {confirmingKey === entry.stableKey ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Confirm
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onDismiss(entry)}
              className="text-xs py-1.5 px-2.5 rounded-md border font-semibold flex items-center gap-1 bg-white text-slate-500 border-slate-200 disabled:opacity-50"
            >
              <X size={12} /> Dismiss
            </button>
          </>
        ) : null}
        {steps.length > 0 && entry.status !== 'suggested' ? (
          <select
            value={entry.connectedStepId ?? ''}
            disabled={busy}
            onChange={(e) => onConnectStep(entry, Number(e.target.value) || null)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 max-w-[200px]"
            title="Connect to investigation step"
          >
            <option value="">Connect step…</option>
            {steps.map((s) => (
              <option key={s.id} value={s.id}>{s.title}</option>
            ))}
          </select>
        ) : null}
      </div>
    </div>
  )
}

export default function PatronActivityTimeline({
  caseId,
  patronActivities = [],
  steps = [],
  incidentDate,
  memberNumber = '',
  onRefresh,
  onAddObservation,
  onLinkFloorData,
}) {
  const [scopeFilter, setScopeFilter] = useState('incident_day')
  const [layoutView, setLayoutView] = useState('timeline')
  const [suggestions, setSuggestions] = useState([])
  const [dismissedKeys, setDismissedKeys] = useState(() => loadDismissedSuggestionKeys(caseId))
  const [loadingSuggest, setLoadingSuggest] = useState(false)
  const [suggestError, setSuggestError] = useState(null)
  const [confirmingKey, setConfirmingKey] = useState(null)
  const [connectingKey, setConnectingKey] = useState(null)
  const [stepConnections, setStepConnections] = useState({})
  const autoSuggestedRef = useRef(false)

  const incidentDayYmd = toLocalDateYmd(incidentDate)

  useEffect(() => {
    autoSuggestedRef.current = false
    setDismissedKeys(loadDismissedSuggestionKeys(caseId))
    setSuggestions([])
    setStepConnections({})
  }, [caseId])

  const stepTitleById = useMemo(() => {
    const m = new Map()
    for (const s of steps) m.set(s.id, s.title)
    return m
  }, [steps])

  const confirmedEntries = useMemo(() => {
    const items = Array.isArray(patronActivities) ? patronActivities : []
    return items
      .map((it) =>
        buildTimelineEntryFromPatronActivity(it, {
          incidentDayYmd,
          steps,
        })
      )
      .filter(Boolean)
      .map((e) => ({
        ...e,
        connectedStepId: stepConnections[e.stableKey] ?? e.connectedStepId,
      }))
  }, [patronActivities, incidentDayYmd, steps, stepConnections])

  const suggestedEntries = useMemo(
    () =>
      suggestions
        .map((it) => buildTimelineEntryFromSuggestion(it, { incidentDayYmd, steps }))
        .filter(Boolean),
    [suggestions, incidentDayYmd, steps]
  )

  const timeline = useMemo(
    () => mergePatronActivityTimeline(confirmedEntries, suggestedEntries, dismissedKeys),
    [confirmedEntries, suggestedEntries, dismissedKeys]
  )

  const filteredTimeline = useMemo(
    () => filterTimelineByScope(timeline, scopeFilter),
    [timeline, scopeFilter]
  )

  const layoutGroups = useMemo(
    () => groupTimelineForLayout(filteredTimeline, layoutView),
    [filteredTimeline, layoutView]
  )

  const loadSuggestions = useCallback(async () => {
    if (!caseId) return
    setLoadingSuggest(true)
    setSuggestError(null)
    try {
      if (!memberNumber?.trim() && !incidentDayYmd) {
        setSuggestError('Member and incident day are needed to suggest activity.')
        setSuggestions([])
        return
      }
      const base = new URLSearchParams()
      base.set('lane', 'combined')
      if (memberNumber?.trim()) base.set('member_number', memberNumber.trim())
      if (incidentDayYmd) {
        base.set('date_from', incidentDayYmd)
        base.set('date_to', incidentDayYmd)
      }
      const fetchView = async (view) => {
        const params = new URLSearchParams(base)
        params.set('view', view)
        const res = await authFetch(
          getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/linkable-evidence?${params}`)
        )
        const json = await res.json()
        if (!json.success) throw new Error(json.error || `Failed to load ${view} activity`)
        return Array.isArray(json.data) ? json.data : []
      }
      const [sessions, intervals] = await Promise.all([fetchView('session'), fetchView('interval')])
      setSuggestions([...sessions, ...intervals])
    } catch (e) {
      setSuggestError(e.message || 'Failed to load suggestions')
      setSuggestions([])
    } finally {
      setLoadingSuggest(false)
    }
  }, [caseId, memberNumber, incidentDayYmd])

  useEffect(() => {
    if (autoSuggestedRef.current) return
    if (!caseId || !memberNumber?.trim() || !incidentDayYmd) return
    autoSuggestedRef.current = true
    loadSuggestions()
  }, [caseId, memberNumber, incidentDayYmd, loadSuggestions])

  const handleDismiss = useCallback(
    (entry) => {
      if (!entry?.stableKey) return
      setDismissedKeys((prev) => {
        const next = new Set(prev)
        next.add(entry.stableKey)
        saveDismissedSuggestionKeys(caseId, next)
        return next
      })
    },
    [caseId]
  )

  const handleConfirm = useCallback(
    async (entry) => {
      if (!caseId || !entry?.browseItem) return
      setConfirmingKey(entry.stableKey)
      try {
        const bulk = buildEvidenceBulkItem(entry.browseItem)
        if (bulk.type === 'combined_session') {
          bulk.expand_intervals = false
        }
        const body = {
          items: [
            {
              ...bulk,
              payload: {
                ...(bulk.payload || {}),
                activity_status: 'confirmed',
                _connected_step_id: entry.connectedStepId || undefined,
              },
            },
          ],
        }
        if (entry.connectedStepId) body.step_id = entry.connectedStepId
        const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/evidence/add`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to link activity')
        if (entry.stableKey && entry.connectedStepId) {
          setStepConnections((prev) => ({ ...prev, [entry.stableKey]: entry.connectedStepId }))
        }
        await onRefresh?.()
      } catch (e) {
        window.alert(e.message || 'Failed to confirm activity')
      } finally {
        setConfirmingKey(null)
      }
    },
    [caseId, onRefresh]
  )

  const handleConnectStep = useCallback((entry, stepId) => {
    if (!entry?.stableKey) return
    setConnectingKey(entry.stableKey)
    setStepConnections((prev) => {
      const next = { ...prev }
      if (stepId) next[entry.stableKey] = stepId
      else delete next[entry.stableKey]
      return next
    })
    setConnectingKey(null)
  }, [])

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden mb-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <span aria-hidden>📋</span> Patron activity
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Floor activity observations live here. Link sessions, intervals, and payouts from the floor — they stay on Linked Transactions until attached to an activity.
            {incidentDayYmd ? (
              <span className="block mt-0.5">
                Incident day: <strong>{incidentDayYmd}</strong>
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={loadSuggestions}
            disabled={loadingSuggest}
            className="text-xs py-1.5 px-3 rounded-md border font-semibold flex items-center gap-1 disabled:opacity-50"
            style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
          >
            {loadingSuggest ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Suggest incident-day activity
          </button>
          {onAddObservation ? (
            <button
              type="button"
              onClick={onAddObservation}
              className="text-xs py-1.5 px-3 rounded-md border font-semibold flex items-center gap-1 disabled:opacity-50"
              style={{ borderColor: P.pri, background: '#fff', color: P.pri }}
            >
              <Plus size={12} /> Add
            </button>
          ) : null}
          {onLinkFloorData ? (
            <button
              type="button"
              onClick={onLinkFloorData}
              className="text-xs py-1.5 px-3 rounded-md border font-semibold flex items-center gap-1 bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            >
              Link floor data
            </button>
          ) : null}
        </div>
      </div>

      <div className="px-4 pt-3 flex flex-wrap gap-1.5 items-center">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1">Scope</span>
        {SCOPE_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setScopeFilter(f.id)}
            className={`text-xs py-1 px-2.5 rounded-full font-semibold border ${
              scopeFilter === f.id
                ? 'border-[#0a5169] bg-[#e8f2f6] text-[#0a5169]'
                : 'border-slate-200 bg-white text-slate-500'
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="w-px h-4 bg-slate-200 mx-1 hidden sm:block" />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1">View</span>
        {LAYOUT_VIEWS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setLayoutView(f.id)}
            className={`text-xs py-1 px-2.5 rounded-full font-semibold border ${
              layoutView === f.id
                ? 'border-[#0a5169] bg-[#e8f2f6] text-[#0a5169]'
                : 'border-slate-200 bg-white text-slate-500'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {suggestError ? (
          <div className="text-xs text-red-600 mb-3 p-2 rounded-md bg-red-50 border border-red-100">{suggestError}</div>
        ) : null}

        {filteredTimeline.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-xs border-2 border-dashed border-slate-200 rounded-lg">
            {timeline.length === 0 ? (
              <>
                No patron activity linked yet.
                {memberNumber ? (
                  <span className="block mt-1">
                    Click <strong>Add</strong> to record what you observed, or <strong>Link floor data</strong> for sessions and intervals.
                  </span>
                ) : (
                  <span className="block mt-1">
                    Confirm a case subject (member) to auto-suggest incident-day sessions and intervals.
                  </span>
                )}
              </>
            ) : (
              'No rows match this filter.'
            )}
          </div>
        ) : (
          layoutGroups.map((group) => (
            <div key={group.key} className="mb-4 last:mb-0">
              {group.label ? (
                <div className="flex items-center gap-2 mb-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <span className="flex-1 border-t border-slate-200" />
                  {group.label}
                  <span className="flex-1 border-t border-slate-200" />
                </div>
              ) : null}
              {(() => {
                let lastDayDivider = null
                return group.entries.map((entry) => {
                  const divider =
                    layoutView === 'timeline' && entry.dayYmd && entry.dayYmd !== lastDayDivider ? (
                      <div
                        key={`div-${group.key}-${entry.dayYmd}`}
                        className="flex items-center gap-2 my-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider"
                      >
                        <span className="flex-1 border-t border-slate-200" />
                        {entry.dayYmd}
                        <span className="flex-1 border-t border-slate-200" />
                      </div>
                    ) : null
                  if (entry.dayYmd) lastDayDivider = entry.dayYmd
                  return (
                    <React.Fragment key={`${group.key}-${entry.id || entry.stableKey}`}>
                      {divider}
                      <TimelineRow
                        caseId={caseId}
                        entry={entry}
                        steps={steps}
                        stepTitleById={stepTitleById}
                        onConfirm={handleConfirm}
                        onDismiss={handleDismiss}
                        onConnectStep={handleConnectStep}
                        confirmingKey={confirmingKey}
                        connectingKey={connectingKey}
                      />
                    </React.Fragment>
                  )
                })
              })()}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
